import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatErr } from "../../../../../lib/api";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../../../components/ui/table";
import { toast } from "sonner";
import { SectionCard, EmptyState, DetailField } from "../shared";
import SearchableSelect from "../SearchableSelect";
import { ACCOUNTING_BASE } from "../accountingNav";
import { ReportFilterBar, ReportPrintShell, useFinancialYears, downloadReportCsv, formatCurrency, formatBalance, formatDateDMY } from "./reportShared";

/**
 * Phase 8 - Bank Book (items 10-15, 19, 45, 46).
 *
 * The user selects a real physical Accounting Bank Account (never a raw
 * ledger) - the backend resolves AccountingBankAccount -> linkedLedgerId
 * itself, so this behaves like a genuine bank statement from the books.
 * Each bank account is fully independent (item 15) - selecting a different
 * one shows that account's own opening/closing/rows, never a merged view.
 */
export default function BankBookPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const financialYears = useFinancialYears();
  const [accounts, setAccounts] = useState([]);
  const [bankAccountId, setBankAccountId] = useState(searchParams.get("bankAccountId") || "");
  const [financialYearId, setFinancialYearId] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/society/accounting/engine/bank-accounts");
        const rows = Array.isArray(res.data) ? res.data : [];
        setAccounts(rows);
        if (!bankAccountId && rows.length === 1) setBankAccountId(rows[0].id);
      } catch { setAccounts([]); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const accountOptions = useMemo(() => accounts.map((a) => ({
    value: a.id, label: `${a.bankName} — ${a.accountName || ""} •••• ${a.accountNumber || ""}`.replace(/\s+/g, " ").trim(),
  })), [accounts]);

  const params = { bankAccountId, financialYearId, fromDate, toDate };

  const load = async () => {
    if (!bankAccountId) { setData(null); return; }
    try {
      setLoading(true);
      const res = await api.get("/society/accounting/reports/bank-book", { params });
      setData(res.data);
    } catch (err) {
      toast.error(formatErr(err, "Could not load bank book"));
      setData(null);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [bankAccountId, financialYearId, fromDate, toDate]);

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-extrabold text-[#111827]">Bank Book</h2>
        <p className="mt-1 text-sm text-[#6B7280]">Statement view for one physical Bank Account, built from its opening balance and posted vouchers.</p>
      </div>

      <div className="report-screen-only space-y-5">
        <ReportFilterBar
          financialYears={financialYears} financialYearId={financialYearId} onFinancialYearChange={setFinancialYearId}
          fromDate={fromDate} toDate={toDate} onFromDateChange={setFromDate} onToDateChange={setToDate}
          extra={
            <div className="w-72 space-y-1">
              <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#6B7280]">Bank Account</div>
              <SearchableSelect value={bankAccountId} onChange={setBankAccountId} options={accountOptions} placeholder="Select bank account" />
            </div>
          }
          onExport={bankAccountId ? () => downloadReportCsv("/society/accounting/reports/bank-book", params, `bank-book-${bankAccountId}.csv`) : undefined}
          onPrint={data ? () => window.print() : undefined}
        />

        {!bankAccountId ? (
          <EmptyState>Select a Bank Account to view its statement.</EmptyState>
        ) : loading ? (
          <div className="text-sm text-[#6B7280]">Loading...</div>
        ) : !data ? (
          <EmptyState>No data.</EmptyState>
        ) : (
          <SectionCard title={data.bankName} subtitle={`${data.financialYear ? `FY ${data.financialYear} — ` : ""}${data.fromDate} to ${data.toDate}`}>
            <div className="mb-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
              <DetailField label="Account Name" value={data.accountName} />
              <DetailField label="Account No." value={data.accountNumberMasked} />
              <DetailField label="Branch" value={data.branch} />
              <DetailField label="IFSC" value={data.ifsc} />
            </div>
            <div className="mb-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
              <div><div className="text-xs uppercase tracking-[0.14em] text-[#6B7280]">Opening Balance</div><div className="mt-0.5 text-sm font-bold">{formatBalance(data.openingBalance, data.openingSide)}</div></div>
              <div><div className="text-xs uppercase tracking-[0.14em] text-[#6B7280]">Total Debit</div><div className="mt-0.5 text-sm font-bold">{formatCurrency(data.periodDebit)}</div></div>
              <div><div className="text-xs uppercase tracking-[0.14em] text-[#6B7280]">Total Credit</div><div className="mt-0.5 text-sm font-bold">{formatCurrency(data.periodCredit)}</div></div>
              <div><div className="text-xs uppercase tracking-[0.14em] text-[#6B7280]">Closing Balance</div><div className="mt-0.5 text-sm font-bold">{formatBalance(data.closingBalance, data.closingSide)}</div></div>
            </div>
            <div className="overflow-x-auto rounded-sm border border-[#E5E7EB]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead><TableHead>Voucher No.</TableHead><TableHead>Type</TableHead>
                    <TableHead>Particulars</TableHead><TableHead>Party</TableHead><TableHead>Mode</TableHead><TableHead>Reference</TableHead>
                    <TableHead className="text-right">Debit</TableHead><TableHead className="text-right">Credit</TableHead><TableHead className="text-right">Running Balance</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow className="bg-[#F9FAFB]">
                    <TableCell>Opening</TableCell><TableCell colSpan={2}>Opening Balance</TableCell><TableCell colSpan={4}>—</TableCell>
                    <TableCell className="text-right">—</TableCell><TableCell className="text-right">—</TableCell>
                    <TableCell className="text-right font-semibold">{formatBalance(data.openingBalance, data.openingSide)}</TableCell>
                  </TableRow>
                  {data.rows.map((row, i) => (
                    <TableRow key={i}>
                      <TableCell>{formatDateDMY(row.voucherDate)}</TableCell>
                      <TableCell>
                        {row.voucherId ? <button type="button" className="font-semibold text-[#111827] hover:underline" onClick={() => navigate(`${ACCOUNTING_BASE}/vouchers/${row.voucherId}`)}>{row.voucherNumber || "(draft)"}</button> : "-"}
                      </TableCell>
                      <TableCell>{row.voucherType}</TableCell>
                      <TableCell>{row.narration}</TableCell>
                      <TableCell>{row.partyName || "-"}</TableCell>
                      <TableCell>{row.paymentMode || "-"}</TableCell>
                      <TableCell>{row.referenceNo || "-"}</TableCell>
                      <TableCell className="text-right">{row.debit ? formatCurrency(row.debit) : "—"}</TableCell>
                      <TableCell className="text-right">{row.credit ? formatCurrency(row.credit) : "—"}</TableCell>
                      <TableCell className="text-right font-semibold">{formatBalance(row.runningBalance, row.runningSide)}</TableCell>
                    </TableRow>
                  ))}
                  {data.rows.length === 0 && <TableRow><TableCell colSpan={10} className="text-center text-sm text-[#6B7280]">No transactions in this range.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
          </SectionCard>
        )}
      </div>

      {data && (
        <div className="report-print-only">
          <ReportPrintShell reportName={`Bank Book — ${data.bankName}`} rangeLabel={`Account: ${data.accountName || ""} •••• ${data.accountNumberMasked || ""} — ${data.fromDate} to ${data.toDate}`}>
            <table className="w-full border-collapse text-[10px]">
              <thead><tr className="border-b border-black"><th className="py-1 text-left">Date</th><th className="py-1 text-left">Voucher</th><th className="py-1 text-left">Particulars</th><th className="py-1 text-left">Ref</th><th className="py-1 text-right">Debit</th><th className="py-1 text-right">Credit</th><th className="py-1 text-right">Balance</th></tr></thead>
              <tbody>
                <tr className="border-b border-gray-300"><td className="py-0.5">Opening</td><td /><td className="py-0.5">Opening Balance</td><td /><td /><td /><td className="py-0.5 text-right">{formatBalance(data.openingBalance, data.openingSide)}</td></tr>
                {data.rows.map((row, i) => (
                  <tr key={i} className="border-b border-gray-300">
                    <td className="py-0.5">{formatDateDMY(row.voucherDate)}</td>
                    <td className="py-0.5">{row.voucherNumber}</td>
                    <td className="py-0.5">{row.narration}</td>
                    <td className="py-0.5">{row.referenceNo}</td>
                    <td className="py-0.5 text-right">{row.debit ? formatCurrency(row.debit) : ""}</td>
                    <td className="py-0.5 text-right">{row.credit ? formatCurrency(row.credit) : ""}</td>
                    <td className="py-0.5 text-right">{formatBalance(row.runningBalance, row.runningSide)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ReportPrintShell>
        </div>
      )}
    </div>
  );
}
